import { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, User, Phone, MapPin, Mail, CreditCard, FileText } from 'lucide-react';
import useCustomerStore from '../../stores/useCustomerStore';

/**
 * Customer Modal Component
 * إضافة/تعديل عميل
 *
 * Protocols Applied (From Father DB):
 * - P-FORM-001: Form Management
 * - P034: Data Validation
 * - P010: Error Handling
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode
 * - P-UX-001: User Experience
 * - CUST-001: Customer CRUD
 * - CUST-002: Credit Limit Management
 */
const CustomerModal = ({ customer, onClose }) => {
  const { addCustomer, updateCustomer } = useCustomerStore();

  const isEditMode = Boolean(customer);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    email: '',
    creditLimit: 5000,
    notes: '',
    status: 'active'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load customer data in edit mode
  useEffect(() => {
    if (customer) {
      // Convert status from integer to string for select
      const statusFromInt = customer.status === 1 || customer.status === '1' ? 'active' : 'inactive';

      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        address: customer.address || '', // ✅ Fixed: was missing
        email: customer.email || '',
        creditLimit: customer.credit_limit || customer.creditLimit || 5000, // ✅ Fixed: support both formats
        notes: customer.notes || '',
        status: typeof customer.status === 'string' ? customer.status : statusFromInt
      });
    }
  }, [customer]);

  // Validation (P034)
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'اسم العميل مطلوب';
    }

    // Phone validation (Egyptian format)
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    } else {
      // Egyptian phone: 01[0-2,5]{1}[0-9]{8}
      const phoneRegex = /^01[0-2,5]{1}[0-9]{8}$/;
      if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = 'رقم الهاتف غير صحيح (يجب أن يبدأ بـ 01 ويتكون من 11 رقم)';
      }
    }

    // Email validation (optional but if provided must be valid)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'البريد الإلكتروني غير صحيح';
      }
    }

    // Credit Limit validation
    const creditLimit = parseFloat(formData.creditLimit);
    if (isNaN(creditLimit) || creditLimit < 0) {
      newErrors.creditLimit = 'الحد الائتماني يجب أن يكون رقم موجب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle Submit (P010: Error Handling)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert status to integer for API (1=active, 0=inactive)
      const statusMap = {
        'active': 1,
        'inactive': 0,
        'blocked': 0
      };

      // Convert camelCase to snake_case for API
      const dataToSubmit = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        email: formData.email,
        credit_limit: parseFloat(formData.creditLimit) || 0, // Convert to snake_case
        notes: formData.notes,
        status: statusMap[formData.status] ?? 1 // Default to active
      };

      if (isEditMode) {
        // Update existing customer
        await updateCustomer(customer.id, dataToSubmit);
      } else {
        // Add new customer
        await addCustomer(dataToSubmit);
      }

      // Close modal on success and trigger success toast
      onClose(true);
    } catch (error) {
      console.error('Customer save error:', error);

      // Handle validation errors from backend
      if (error.response && error.response.data && error.response.data.errors) {
        // Backend validation errors (Laravel format)
        const backendErrors = error.response.data.errors;
        const mappedErrors = {};

        // Map backend field names to frontend field names
        Object.keys(backendErrors).forEach(key => {
          const frontendKey = key === 'credit_limit' ? 'creditLimit' : key;
          mappedErrors[frontendKey] = Array.isArray(backendErrors[key])
            ? backendErrors[key][0]
            : backendErrors[key];
        });

        setErrors(mappedErrors);
      } else {
        // Generic error
        setErrors({
          submit: error.response?.data?.message || error.message || 'حدث خطأ أثناء الحفظ'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? 'تعديل العميل' : 'إضافة عميل جديد'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                اسم العميل *
              </div>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="مثال: محمد أحمد"
              className={`w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white ${
                errors.name
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            />
            {errors.name && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                رقم الهاتف *
              </div>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="01012345678"
              dir="ltr"
              maxLength={11}
              className={`w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white font-mono ${
                errors.phone
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            />
            {errors.phone && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {errors.phone}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              يجب أن يبدأ بـ 01 ويتكون من 11 رقم
            </p>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                العنوان
              </div>
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="مثال: 15 شارع الجمهورية، القاهرة"
              rows={2}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white resize-none"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                البريد الإلكتروني
              </div>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@email.com"
              dir="ltr"
              className={`w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white ${
                errors.email
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            />
            {errors.email && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Credit Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                الحد الائتماني (ج.م) *
              </div>
            </label>
            <input
              type="number"
              name="creditLimit"
              value={formData.creditLimit}
              onChange={handleChange}
              placeholder="5000"
              min="0"
              step="100"
              className={`w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white ${
                errors.creditLimit
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            />
            {errors.creditLimit && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {errors.creditLimit}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              الحد الأقصى للمبلغ المستحق على العميل
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              الحالة
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
            >
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
              <option value="blocked">محظور</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                ملاحظات
              </div>
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="أي ملاحظات إضافية..."
              rows={3}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white resize-none"
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{errors.submit}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
          >
            إلغاء
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                جاري الحفظ...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'حفظ التعديلات' : 'إضافة العميل'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerModal;
